import React, { useState, useEffect } from 'react'
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  CardActions,
  Modal,
  Box,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  IconButton,
  Grid,
  useMediaQuery,
  Badge,
} from '@mui/material'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import InventoryIcon from '@mui/icons-material/Inventory'
import LocalFloristIcon from '@mui/icons-material/LocalFlorist'
import CloseIcon from '@mui/icons-material/Close'
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive'
import { API_URL, isAuthenticated } from '../../config/config'
import globalState from '../../shared/globalState'
import api from '../../config/api'
import SizeChip from '../Chips/ChipSize'

function getMultiplesFast(quantity, multiplicity) {
  const count = Math.floor(quantity / multiplicity)
  const result = new Array(count)

  for (let i = 1; i <= count; i += 1) {
    result[i - 1] = i * multiplicity
  }

  return result
}

const ProductCard = React.memo(({ product: initialProduct }) => {
  const [openModal, setOpenModal] = useState(false)
  const [product, setProduct] = useState(initialProduct)
  const [quantity, setQuantity] = useState(initialProduct?.category?.stack || 1)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const isMobile = useMediaQuery('(max-width:600px)')

  // Обновляем локальное состояние при изменении пропса
  useEffect(() => {
    setProduct(initialProduct)
    setQuantity(initialProduct?.category?.stack || 1)
  }, [initialProduct])

  const price = parseFloat(product?.price) || 0
  const quantityOptions = getMultiplesFast(
    product?.count || 0,
    product?.category?.stack || 1,
  )
  const totalPrice = (quantity * price).toFixed(2)

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const handleAdd = async (e) => {
    e.stopPropagation()

    if (!isAuthenticated()) {
      globalState.setState('infoModal', true)
      return
    }

    try {
      const response = await api.post('/API_FRONT/cart/add/', {
        id: product?.id,
        quantity,
      })

      if (response?.status === 200) {
        setOpenModal(false)
        setSnackbar({
          open: true,
          message: 'Товар успешно добавлен!',
          severity: 'success',
        })
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при добавлении товара!',
        severity: 'error',
      })
    }
  }

  if (!product) return null

  return (
    <>
      <Card
        onClick={() => setOpenModal(true)}
        sx={{
          maxWidth: isMobile ? '100%' : 345,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 3,
          },
        }}
      >
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <CardMedia
            sx={{
              height: 200,
              p: 2,
              width: '100%',
              backgroundColor: '#f5f5f5',
              padding: '0 0 10px 0',
            }}
            image={`${API_URL}/media/${product.image}`}
            title={product.name}
            component="img"
          />
          <Box
            sx={{
              display: 'inline-block',
              bgcolor: '#90FC8A',
              color: 'black',
              px: 2,
              py: 1,
              borderRadius: 1,
              textAlign: 'center',
              width: '100%',
              minHeight: product.payload_arrive_date ? '20px' : '30px',
              overflow: 'hidden',
              opacity: product.payload_arrive_date ? 1 : 0,
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {product.payload_arrive_date}
            </Typography>
          </Box>
        </Box>
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography gutterBottom variant="h6" component="div" noWrap>
            {product.name}
          </Typography>

          {product.size && <SizeChip size={product.size} />}

          {product.payload_arrive_date && false && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <AirplanemodeActiveIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Поставка: {product.payload_arrive_date}
              </Typography>
            </Box>
          )}

          {product?.category?.title && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <LocalFloristIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {product?.category?.title}
              </Typography>
            </Box>
          )}

          {isAuthenticated() && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <InventoryIcon
                fontSize="small"
                color={product.count > 0 ? 'primary' : 'error'}
              />
              <Typography
                variant="body2"
                color={product.count > 0 ? 'text.secondary' : 'error'}
              >
                {product.count > 0
                  ? `В наличии: ${product.count} шт`
                  : 'Нет в наличии'}
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<ShoppingCartIcon />}
            onClick={(e) => {
              e.stopPropagation()
              setOpenModal(true)
            }}
            fullWidth
            disabled={product.count <= 0}
          >
            {price.toFixed(2)} ₽
          </Button>
        </CardActions>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="product-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : 800,
            maxWidth: '95vw',
            maxHeight: '95vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'auto',
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenModal(false)
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Grid container>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: isMobile ? 2 : 4,
                backgroundColor: '#fafafa',
              }}
            >
              <CardMedia
                component="img"
                image={`${API_URL}/media/${product.image}`}
                alt={product.name}
                sx={{
                  maxHeight: 400,
                  width: '100%',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                p: isMobile ? 2 : 4,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {product.name}
              </Typography>

              {product.payload_arrive_date && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  <strong>Поставка:</strong> {product.payload_arrive_date}
                </Typography>
              )}

              {product.size && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  <strong>Размер:</strong> {product.size}
                </Typography>
              )}

              {product.bonus_size && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  <strong>Размер №2:</strong> {product.bonus_size}
                </Typography>
              )}

              {product.external_description && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  <strong>Качество: </strong> {product.external_description}
                </Typography>
              )}

              {product?.category?.stack && product.count > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" mb={1}>
                    <strong>Количество:</strong>
                  </Typography>
                  <Select
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    size="medium"
                    fullWidth
                  >
                    {quantityOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option} шт
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              <Typography variant="h6" mb={3}>
                <strong>Итого:</strong> {totalPrice} ₽
              </Typography>
              {isAuthenticated() && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleAdd}
                  sx={{ mt: 'auto' }}
                  disabled={product.count <= 0}
                >
                  {product.count > 0
                    ? 'Добавить в корзину'
                    : 'Товар закончился'}
                </Button>
              )}
              {!isAuthenticated() && (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleAdd}
                  sx={{ mt: 'auto' }}
                  disabled={product.count <= 0}
                >
                  Авторизоваться
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  )
})

export default ProductCard
